import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Tag, Trash2, Pencil, Check, X } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const AdminTags = () => {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const confirm = useConfirm();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/tags");
            setTags(data);
        } catch {
            toast.error("Failed to load tags");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        try {
            await api.post("/admin/tags", { name: newName.trim() });
            toast.success("Tag created");
            setNewName("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create tag");
        } finally {
            setCreating(false);
        }
    };

    const startEdit = (t) => {
        setEditingId(t.id);
        setEditName(t.name);
    };

    const saveEdit = async (id) => {
        try {
            await api.patch(`/admin/tags/${id}`, { name: editName.trim() });
            toast.success("Tag renamed");
            setEditingId(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Rename failed");
        }
    };

    const remove = async (t) => {
        const ok = await confirm({
            title: `Delete tag "${t.name}"?`,
            message: `Codes and clients using this tag will keep their access, but will be un-tagged. This cannot be undone.`,
            confirmLabel: "Delete Tag",
            confirmVariant: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/admin/tags/${t.id}`);
            toast.success("Tag deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="admin-tags">
            <div className="mb-8">
                <div className="un-label text-un-grape mb-3">Admin · Tags</div>
                <h1 className="un-h1 text-5xl md:text-6xl">
                    Tag <span className="text-un-berry">Manager</span>.
                </h1>
                <p className="text-lg text-un-onyx/70 mt-3 max-w-2xl">
                    Organize activation codes by cohort, launch, or referral source. Tags stay in sync across codes and clients.
                </p>
            </div>

            <form
                onSubmit={create}
                className="un-card p-6 mb-10 flex flex-col md:flex-row md:items-end gap-4"
                data-testid="new-tag-form"
            >
                <label className="block flex-1">
                    <span className="un-label text-un-onyx block mb-2">New Tag Name</span>
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="un-input"
                        placeholder="e.g. Q1-Launch-2026"
                        data-testid="new-tag-input"
                    />
                </label>
                <button
                    type="submit"
                    disabled={creating || !newName.trim()}
                    className="un-btn-primary flex items-center gap-2 justify-center"
                    data-testid="create-tag-button"
                >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Create Tag
                </button>
            </form>

            {loading ? (
                <div className="un-card p-10 flex items-center gap-3 justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-un-berry" />
                </div>
            ) : tags.length === 0 ? (
                <div className="un-card p-10 text-center">
                    <Tag className="w-6 h-6 mx-auto mb-3 text-un-onyx/40" />
                    <div className="un-h3 mb-1">No Tags Yet</div>
                    <div className="text-un-onyx/60 text-sm">Create your first tag above, or tag codes as you generate them.</div>
                </div>
            ) : (
                <div className="border-2 border-un-onyx overflow-x-auto" data-testid="tags-table">
                    <table className="w-full text-left">
                        <thead className="bg-un-onyx text-white">
                            <tr>
                                {["Tag", "Codes", "Clients", "Created", ""].map((h) => (
                                    <th key={h} className="un-label px-4 py-3 border-r-2 border-white/20 last:border-r-0">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tags.map((t) => (
                                <tr key={t.id} className="border-t-2 border-un-onyx" data-testid={`tag-row-${t.id}`}>
                                    <td className="px-4 py-4">
                                        {editingId === t.id ? (
                                            <input
                                                autoFocus
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                className="un-input un-mono text-sm"
                                                data-testid={`tag-edit-input-${t.id}`}
                                            />
                                        ) : (
                                            <span className="un-mono font-bold text-un-berry">{t.name}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-4 un-mono text-sm">{t.code_count}</td>
                                    <td className="px-4 py-4 un-mono text-sm">{t.client_count}</td>
                                    <td className="px-4 py-4 un-mono text-xs text-un-onyx/60 whitespace-nowrap">
                                        {new Date(t.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                                        {editingId === t.id ? (
                                            <>
                                                <button
                                                    onClick={() => saveEdit(t.id)}
                                                    className="un-btn-primary text-xs inline-flex items-center gap-1"
                                                    data-testid={`tag-save-${t.id}`}
                                                >
                                                    <Check className="w-3 h-3" /> Save
                                                </button>
                                                <button
                                                    onClick={() => setEditingId(null)}
                                                    className="un-btn-ghost text-xs inline-flex items-center gap-1"
                                                >
                                                    <X className="w-3 h-3" /> Cancel
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => startEdit(t)}
                                                    className="un-btn-ghost text-xs inline-flex items-center gap-1"
                                                    data-testid={`tag-rename-${t.id}`}
                                                >
                                                    <Pencil className="w-3 h-3" /> Rename
                                                </button>
                                                <button
                                                    onClick={() => remove(t)}
                                                    className="un-btn-outline text-xs inline-flex items-center gap-1"
                                                    data-testid={`tag-delete-${t.id}`}
                                                >
                                                    <Trash2 className="w-3 h-3" /> Delete
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminTags;
